import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocationPicker } from './LocationPicker';
import { DatePicker } from './DatePicker';
import { MobileSearchOverlay } from './MobileSearchOverlay';
import { useLocationStore } from '@/stores/useLocationStore';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface SearchWidgetProps {
    /** 'hero' for homepage (dark bg), 'light' for catalogue (light bg) */
    variant?: 'hero' | 'light';
    /** Compact mode puts everything on one line */
    compact?: boolean;
}

export const SearchWidget = ({ variant: _variant = 'hero', compact = false }: SearchWidgetProps) => {
    const navigate = useNavigate();
    const { location, dateRange, dateMode, selectedTier, pickupDate, setDateMode, setDateRange } = useLocationStore();
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    /**
     * Get display text for flexible mode state
     */
    const getFlexibleModeDisplay = (): string => {
        if (selectedTier && pickupDate) {
            return `${selectedTier.tierName} from ${format(pickupDate, 'MMM d')}`;
        }
        if (selectedTier) {
            return `${selectedTier.tierName} rental`;
        }
        return 'No dates set';
    };

    const handleSearch = () => {
        navigate('/catalogue');
    };

    const isValid = !!location;

    const getSearchButtonLabel = () => {
        if (dateMode === 'flexible' || (!dateRange.from && !dateRange.to)) return 'Browse catalogue';
        return 'Search';
    };

    const handleClearDates = () => {
        setDateRange({ from: undefined, to: undefined });
        setDateMode('flexible');
    };

    const hasDates = !!(dateRange.from || dateRange.to);

    const isHero = _variant === 'hero';

    // Compact inline layout for catalogue header
    if (compact) {
        return (
            <div className="flex items-center gap-2 w-full">
                {/* Compact Search Bar */}
                <div className={isHero
                    ? 'flex flex-1 items-center rounded-full border border-border/80 bg-card px-3 py-1.5 shadow-card'
                    : 'flex flex-1 items-center rounded-full border border-border bg-card px-3 py-1.5 shadow-sm'}>
                    <div className={isHero ? 'min-w-0 flex-[2] border-r border-border pr-3' : 'min-w-0 flex-[2] border-r border-border pr-3'}>
                        <LocationPicker
                            label=""
                            placeholder="Pickup location"
                            compact
                            showHelperText={false}
                        />
                    </div>
                    <div className="min-w-0 flex-1 px-3">
                        {dateMode === 'fixed' ? (
                            <div className="flex items-center gap-1">
                                <DatePicker compact />
                                {hasDates && (
                                    <button
                                        onClick={handleClearDates}
                                        className="inline-flex min-h-[44px] items-center gap-1 rounded-full px-2 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/10"
                                        aria-label="Clear selected dates"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={() => setDateMode('fixed')}
                                className="flex min-h-[44px] items-center gap-1 rounded-full px-2 text-xs text-muted-foreground hover:bg-secondary hover:text-primary"
                                title={selectedTier ? getFlexibleModeDisplay() : 'Add dates'}
                            >
                                <HelpCircle className="h-3 w-3" />
                                <span className="hidden sm:inline truncate max-w-[120px]">
                                    {selectedTier ? selectedTier.tierName : 'Add dates'}
                                </span>
                            </button>
                        )}
                    </div>
                    <Button
                        size="sm"
                        className="h-11 min-h-[44px] min-w-[44px] rounded-full px-0"
                        onClick={handleSearch}
                        disabled={!isValid}
                        aria-label={getSearchButtonLabel()}
                    >
                        <Search className="h-3 w-3" />
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className={isHero ? 'flex w-full flex-col' : 'flex w-full max-w-3xl flex-col items-center'}>
            {/* Desktop Search Bar */}
            <div
                data-testid={isHero ? 'hero-search-shell' : undefined}
                className={isHero
                    ? 'hidden w-full items-center rounded-[2rem] border border-border/80 bg-card/95 p-2 shadow-[0_22px_50px_rgba(54,49,43,0.16)] transition-[border-color,box-shadow,transform] duration-200 hover:border-primary/20 motion-safe:hover:-translate-y-0.5 hover:shadow-[0_26px_56px_rgba(54,49,43,0.18)] md:flex'
                    : 'hidden w-full items-center rounded-full border border-border bg-white p-2 shadow-lg transition-shadow hover:shadow-xl md:flex'}
            >
                <div className={isHero
                    ? 'min-w-0 flex-[1.5] cursor-pointer rounded-[1.4rem] px-6 py-3 hover:bg-secondary/70'
                    : 'flex-1 cursor-pointer rounded-full px-6 py-2 hover:bg-secondary/70'}>
                    <LocationPicker
                        label="Where do you need the equipment?"
                        placeholder="Choose your pickup destination"
                        showHelperText
                    />
                </div>

                {/* Straight divider between the two cells. Deliberately a
                    separate element: a `border-r` on the (rounded) location
                    cell curves at both ends and reads as a stray arc. */}
                <div
                    data-testid="search-divider"
                    aria-hidden="true"
                    className="mx-1 h-10 w-px flex-shrink-0 self-center bg-border"
                />

                {/* Date Picker — always visible, with clear button when dates are set */}
                <div className="flex-1 px-4 py-2">
                    <div className="flex items-center gap-2">
                        <div className={cn(isHero ? 'flex-1 cursor-pointer rounded-2xl hover:bg-secondary/70' : 'flex-1 cursor-pointer rounded hover:bg-secondary/70')}>
                            <DatePicker />
                        </div>
                        {hasDates && (
                            <button
                                onClick={handleClearDates}
                                className="inline-flex min-h-[44px] items-center rounded-full px-3 text-xs font-semibold text-muted-foreground underline underline-offset-2 hover:bg-primary/10 hover:text-primary"
                                title="Clear dates to browse without date filters"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                <div className="pl-2 pr-2">
                    <Button
                        size="lg"
                        className={isHero ? 'gap-2 rounded-[1.4rem] bg-accent px-8 text-accent-foreground transition-[transform,box-shadow,background-color] duration-200 hover:bg-accent/90 motion-safe:hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background' : 'rounded-full px-8 gap-2'}
                        onClick={handleSearch}
                        disabled={!isValid}
                    >
                        <Search className="h-4 w-4" />
                        <span>{getSearchButtonLabel()}</span>
                    </Button>
                </div>
            </div>

            {isHero ? (
                <p
                    data-testid="hero-search-hint-desktop"
                    className="mt-3 hidden px-1 text-sm leading-6 text-foreground/70 md:block"
                >
                    Travelling from Dublin to Alicante? Choose Alicante for collection and return.
                </p>
            ) : null}

            {/* Mobile Search Trigger */}
            <button
                onClick={() => setIsMobileSearchOpen(true)}
                data-testid={isHero ? 'hero-mobile-search-trigger' : undefined}
                className={isHero
                    ? 'flex w-full items-center rounded-[1.75rem] border border-border/80 bg-card/95 p-4 shadow-card transition-[border-color,box-shadow,transform] duration-200 hover:border-primary/20 motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:hidden'
                    : 'flex w-full items-center rounded-full bg-white p-4 shadow-lg md:hidden'}
            >
                <Search className="mr-4 h-5 w-5 text-primary" />
                <div className="flex flex-col items-start">
                    {location ? (
                        <>
                            <span className="text-[0.625rem] font-bold uppercase tracking-[0.18em] text-primary/80">
                                Pickup in
                            </span>
                            <span className="font-display text-base leading-tight text-foreground">
                                {location.name}
                            </span>
                        </>
                    ) : (
                        <span className="text-sm font-semibold text-foreground">
                            Choose pickup location
                        </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                        {dateMode === 'flexible' || !dateRange.from
                            ? selectedTier
                                ? getFlexibleModeDisplay()
                                : 'Add dates to filter availability'
                            : 'Review your dates'
                        }
                    </span>
                </div>
            </button>

            {isHero ? (
                <p
                    data-testid="hero-search-hint-mobile"
                    className="mt-3 px-1 text-sm leading-6 text-foreground/70 md:hidden"
                >
                    Travelling from Dublin to Alicante? Choose Alicante for collection and return.
                </p>
            ) : null}

            <MobileSearchOverlay
                isOpen={isMobileSearchOpen}
                onClose={() => setIsMobileSearchOpen(false)}
                onSearch={handleSearch}
            />
        </div>
    );
};
